import React, { useEffect, useMemo, useRef, useCallback } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import LinkIcon from '@mui/icons-material/Link';
import SyncIcon from '@mui/icons-material/Sync';
import { useDrag, useDrop } from 'react-dnd';
import { getEmptyImage } from 'react-dnd-html5-backend';
import EntityCard from '../common/EntityCard';
import TrendSparkline from '../common/TrendSparkline';
import InsightsPanel from '../common/InsightsPanel';
import StatusChip from '../common/StatusChip';
import { getLinkedAccountIds, isAccountLinked } from '../../utils/categoryAccountUtils';
import { accountBalanceHistory } from '../../utils/entityTrendSeries';
import buildAccountAnalysisPayload from '../../utils/accountAnalysisPayload';
import api from '../../services/api';

/*
  AccountCard: one account as a grid card — identity row (institution, bank
  link, updated time), current balance, the month-end balance history line
  plus the dashed forecast (the Analysis page's per-account forecast, so
  both surfaces show the same figures), and collapsible AI insights. Drag
  anywhere on the card to reorder.
*/

const money2 = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
});

const AccountCard = ({
  account,
  index,
  monthKeys,
  forecast,
  transactions,
  lastUpdated,
  onEdit,
  onDelete,
  onBankRefresh,
  moveCard,
  selectable = false,
  selected = false,
  onToggleSelect,
}) => {
  const ref = useRef(null);

  const [{ isDragging }, drag, preview] = useDrag({
    type: 'ACCOUNT',
    item: () => ({
      index,
      id: account.id,
      name: account.name,
      color: account.color,
      width: ref.current?.offsetWidth,
    }),
    collect: (monitor) => ({ isDragging: monitor.isDragging() }),
  });

  // Suppress the browser's drag snapshot; DragPreviewLayer renders instead
  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: true });
  }, [preview]);

  const [{ handlerId }, drop] = useDrop({
    accept: 'ACCOUNT',
    collect: (monitor) => ({ handlerId: monitor.getHandlerId() }),
    hover: (draggedItem, monitor) => {
      if (!ref.current) return;
      const dragIndex = draggedItem.index;
      const hoverIndex = index;
      if (dragIndex === hoverIndex) return;
      const rect = ref.current.getBoundingClientRect();
      const midX = (rect.right - rect.left) / 2;
      const midY = (rect.bottom - rect.top) / 2;
      const offset = monitor.getClientOffset();
      const x = offset.x - rect.left;
      const y = offset.y - rect.top;
      if (dragIndex < hoverIndex && y < midY && x < midX) return;
      if (dragIndex > hoverIndex && y > midY && x > midX) return;
      moveCard(dragIndex, hoverIndex);
      draggedItem.index = hoverIndex;
    },
  });

  drag(drop(ref));

  const history = useMemo(
    () => accountBalanceHistory(account, transactions, monthKeys),
    [account, transactions, monthKeys]
  );
  const balance = account.balance || 0;
  const hasActivity = useMemo(
    () => history.points.some((p) => p.value !== history.points[history.points.length - 1].value),
    [history]
  );
  const isDebt = account.type === 'credit' || account.type === 'loan';

  const analysisPayload = useMemo(
    () => buildAccountAnalysisPayload(account, history, forecast),
    [account, history, forecast]
  );
  const fetchAnalysis = useCallback(
    async (force) => {
      const { data } = await api.post('/ai/account-analysis', {
        accountId: account.id,
        data: analysisPayload,
        force,
      });
      return data;
    },
    [account.id, analysisPayload]
  );

  const metaParts = [
    account.institution,
    lastUpdated ? `Updated ${lastUpdated}` : 'Not updated yet',
  ].filter(Boolean);

  return (
    <EntityCard
      cardRef={ref}
      handlerId={handlerId}
      isDragging={isDragging}
      canDrag
      color={account.color}
      name={account.name}
      meta={metaParts.join(' · ')}
      dimmed={account.isActive === false}
      chips={
        <>
          {isAccountLinked(account) && (
            <Tooltip
              title={(() => {
                const count = getLinkedAccountIds(account).length;
                return count > 1 ? `Linked to ${count} bank accounts` : 'Linked to a bank account';
              })()}
            >
              <LinkIcon fontSize="small" color="primary" aria-label="Linked to bank" sx={{ flexShrink: 0 }} />
            </Tooltip>
          )}
          {account.isActive === false ? (
            <StatusChip status="warning" label="Inactive" sx={{ height: 20, fontSize: '0.75rem', flexShrink: 0 }} />
          ) : (
            <StatusChip
              status="neutral"
              label={account.type.charAt(0).toUpperCase() + account.type.slice(1)}
              sx={{ height: 20, fontSize: '0.75rem', flexShrink: 0 }}
            />
          )}
        </>
      }
      menuItems={[
        { key: 'edit', label: 'Edit', icon: <EditIcon fontSize="small" />, onClick: () => onEdit(account) },
        ...(onBankRefresh && isAccountLinked(account)
          ? [{ key: 'refresh', label: 'Refresh from bank', icon: <SyncIcon fontSize="small" />, onClick: () => onBankRefresh(account) }]
          : []),
        { key: 'delete', label: 'Delete', icon: <DeleteIcon fontSize="small" />, onClick: () => onDelete(account) },
      ]}
      statLabel={isDebt && balance < 0 ? 'Owing' : 'Balance'}
      statValue={money2.format(balance)}
      chart={
        hasActivity || forecast ? (
          <TrendSparkline
            points={history.points}
            forecast={forecast}
            color={account.color}
            valueLabel="Balance"
          />
        ) : (
          <Box
            sx={{
              height: 74,
              borderRadius: 2,
              border: '1.5px dashed',
              borderColor: 'divider',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Typography variant="caption" color="text.disabled">
              No activity in this window
            </Typography>
          </Box>
        )
      }
      footer={
        <InsightsPanel
          storageKey={`account:${account.id}`}
          fetchAnalysis={fetchAnalysis}
          disabled={!analysisPayload || !hasActivity}
        />
      }
      selectable={selectable}
      selected={selected}
      onToggleSelect={() => onToggleSelect && onToggleSelect(account.id)}
    />
  );
};

export default AccountCard;
